import { InspirationSource, WebhookPayload, WebhookResponse } from '@/types/webhook';
import { supabase } from '@/integrations/supabase/client';
import { addBreadcrumb, logError } from '@/utils/sentry';

/**
 * Extracts clean username and platform from social media URL or username
 */
export function parseProfileUrl(url: string): InspirationSource | null {
  if (!url.trim()) return null;
  
  // Normalize URL
  const cleanUrl = url.trim().toLowerCase();
  
  // Instagram patterns
  if (cleanUrl.includes('instagram.com')) {
    const instagramMatch = cleanUrl.match(/instagram\.com\/([^/?]+)/);
    if (instagramMatch) {
      let username = instagramMatch[1];
      // Remove @ if present at the beginning
      username = username.replace(/^@+/, '');
      // Remove trailing slashes or parameters
      username = username.split('/')[0].split('?')[0];
      return { username, platform: 'instagram' };
    }
  }
  
  // TikTok patterns
  if (cleanUrl.includes('tiktok.com')) {
    const tiktokMatch = cleanUrl.match(/tiktok\.com\/@([^/?]+)/);
    if (tiktokMatch) {
      let username = tiktokMatch[1];
      username = username.split('/')[0].split('?')[0];
      return { username, platform: 'tiktok' };
    }
  }
  
  // YouTube patterns
  if (cleanUrl.includes('youtube.com')) {
    const youtubeMatch = cleanUrl.match(/youtube\.com\/(?:c\/|channel\/|user\/|@)([^/?]+)/);
    if (youtubeMatch) {
      let username = youtubeMatch[1];
      username = username.replace(/^@+/, '');
      username = username.split('/')[0].split('?')[0];
      return { username, platform: 'youtube' };
    }
  }
  
  // If no URL pattern matched, treat as plain username (default to Instagram for now)
  // Only allow alphanumeric, underscores, and dots (valid Instagram username characters)
  const usernamePattern = /^@?([a-z0-9._]+)$/;
  const usernameMatch = cleanUrl.match(usernamePattern);
  if (usernameMatch) {
    let username = usernameMatch[1];
    // Remove @ if present
    username = username.replace(/^@+/, '');
    return { username, platform: 'instagram' };
  }
  
  return null;
}

/**
 * Sends a webhook request for a single inspiration source via Edge Function
 * Fire and forget - we don't rely on the response due to CORS issues
 */
export async function sendWebhookRequest(
  userId: string, 
  source: InspirationSource
): Promise<WebhookResponse> {
  try {
    const payload: WebhookPayload = {
      user_id: userId,
      inspiration_sources: [source]
    };

    addBreadcrumb(`Webhook: triggering for @${source.username}`, 'webhook', { platform: source.platform });

    // Call Edge Function instead of direct n8n webhook
    // This provides authentication, rate limiting, and security
    supabase.functions.invoke('inspiration-webhook', {
      body: payload,
    }).catch(error => {
      // Log but don't block - n8n processes async anyway
      addBreadcrumb(`Webhook: edge function error for @${source.username}`, 'webhook', { error: String(error) });
    });

    // Always return success since n8n processes regardless of errors
    return { success: true, message: 'Webhook triggered' };

  } catch (error) {
    logError(`Webhook: failed to trigger for @${source.username}`, error, { username: source.username, platform: source.platform });
    return { success: true, message: 'Webhook triggered' }; // Still return success
  }
}

/**
 * Simple function to check if sources have been processed by checking the database
 */
export async function checkProcessingStatus(
  userId: string,
  sources: InspirationSource[]
): Promise<{ processed: InspirationSource[], pending: InspirationSource[] }> {
  try {
    const { supabase } = await import('@/integrations/supabase/client');
    
    const processed: InspirationSource[] = [];
    const pending: InspirationSource[] = [];
    
    for (const source of sources) {
      const { data } = await supabase
        .from('scraped_content')
        .select('id')
        .eq('user_id', userId)
        .eq('username', source.username)
        .limit(1);
      
      if (data && data.length > 0) {
        processed.push(source);
      } else {
        pending.push(source);
      }
    }
    
    return { processed, pending };
    
  } catch (error) {
    console.error('Failed to check processing status:', error);
    return { processed: [], pending: sources };
  }
}

/**
 * Triggers webhooks for all sources and shows simple progress
 * Returns immediately - user can check status later or refresh dashboard
 */
export async function processWebhooks(
  userId: string,
  sources: InspirationSource[],
  onProgress: (completed: number, total: number, currentSource?: InspirationSource, isProcessing?: boolean) => void
): Promise<{ successes: InspirationSource[], failures: { source: InspirationSource, error: string }[] }> {
  
  
  // Trigger all webhooks simultaneously
  const webhookPromises = sources.map(async (source, index) => {
    try {
      onProgress(index, sources.length, source, true);
      await sendWebhookRequest(userId, source);
      
      onProgress(index + 1, sources.length, source, false);
      return { success: true, source };
    } catch (error) {
      console.error(`✗ Webhook failed for @${source.username}:`, error);
      return { success: false, source, error: error.message };
    }
  });
  
  // Wait for all webhooks to complete
  const results = await Promise.allSettled(webhookPromises);
  
  const successes: InspirationSource[] = [];
  const failures: { source: InspirationSource, error: string }[] = [];
  
  results.forEach((result, index) => {
    if (result.status === 'fulfilled' && result.value.success) {
      successes.push(result.value.source);
    } else {
      const source = sources[index];
      const error = result.status === 'rejected' ? result.reason?.message : result.value?.error;
      failures.push({ source, error: error || 'Unknown error' });
    }
  });
  
  
  
  return { successes, failures };
}